// 지역 안내·결과·요령의 표시 데이터를 만드는 순수 로직(DOM·three 없음). 문구와 수치의 단일 출처는 shared/regions.ts REGIONS다.
// qaNote(개발 사정)는 화면에 쓰지 않는다: 이 파일은 RegionInfo에서 플레이어 문구 필드만 읽는다.
import { REGION_IDS, isRegionId, type MapId, type Rect, type RegionId } from '../shared/levels';
import { REGIONS, type RegionInfo } from '../shared/regions';
import { Scene, type RegionRecordView } from '../shared/types';
import { fmtNum, fmtTime } from './dom';
import { DIFFICULTY_ILVL_ADD, DIFFICULTY_LOOT } from '../shared/content/economy';
import { mobLevel } from '../shared/content/world';
import { GEM_DROP } from '../shared/content/consumables';
import { TOWN_SPAWN } from '../shared/villageMaps';

/** 마을에서 문 안내 카드를 띄우는 거리(m, 문 사각형 가장자리까지) */
export const EXIT_CARD_RADIUS = 7;
/** 카드가 이미 떠 있는 문은 반경 밖으로 이 값만큼 나가도 유지하고, 다른 문이 이만큼 더 가까워야 바뀐다(경계·두 문 사이에서 깜박이지 않게) */
export const EXIT_CARD_HYST = 0.8;

/** 점에서 사각형까지 거리(안이면 0) */
export function distToRect(r: Rect, x: number, z: number): number {
  const dx = x < r.x0 ? r.x0 - x : x > r.x1 ? x - r.x1 : 0;
  const dz = z < r.z0 ? r.z0 - z : z > r.z1 ? z - r.z1 : 0;
  return Math.hypot(dx, dz);
}

/**
 * 영웅 (x, z)에서 안내 카드를 띄울 문 인덱스. 반경(EXIT_CARD_RADIUS, 포함) 안에서 가장 가까운 문 하나, 없으면 -1.
 * prev(직전 선택)가 있으면 히스테리시스를 둔다. 같은 거리면 앞 인덱스. 할당 없음.
 */
export function pickExit(exits: readonly { readonly rect: Rect }[], x: number, z: number, prev = -1): number {
  let best = -1, bestD = Infinity;
  for (let i = 0; i < exits.length; i++) {
    const d = distToRect(exits[i].rect, x, z);
    if (d < bestD) { bestD = d; best = i; }
  }
  if (prev >= 0 && prev < exits.length) {
    const dp = distToRect(exits[prev].rect, x, z);
    if (dp <= EXIT_CARD_RADIUS + EXIT_CARD_HYST && dp <= bestD + EXIT_CARD_HYST) return prev;
  }
  return bestD <= EXIT_CARD_RADIUS ? best : -1;
}

/** 문이 가리키는 지역(문 목적지가 지역이 아니면 null) */
export function exitRegion(exits: readonly { readonly to: MapId }[], i: number): RegionId | null {
  if (i < 0 || i >= exits.length) return null;
  const to = exits[i].to;
  return isRegionId(to) ? to : null;
}

// ── 기록 표시 ───────────────────────────────────────
/** 최단 시간 mm:ss, 기록이 없거나 값이 잘못됐으면 '-' */
export function fmtBestTime(ticks: number): string {
  return Number.isFinite(ticks) && ticks > 0 ? fmtTime(ticks) : '-';
}

/** 별 0..3을 ★★☆ 꼴로. 범위 밖·잘못된 값은 0..3으로 맞춘다 */
export function starsText(n: number): string {
  const k = Number.isFinite(n) ? Math.max(0, Math.min(3, Math.floor(n))) : 0;
  return '★'.repeat(k) + '☆'.repeat(3 - k);
}

function safeCount(n: number | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** 내 기록 한 줄: 클리어 N회 · 최단 mm:ss · 최고 ★★☆ */
export function recordText(rec: RegionRecordView | undefined): string {
  return '클리어 ' + fmtNum(safeCount(rec?.clears)) + '회 · 최단 ' + fmtBestTime(rec?.bestClearTicks ?? 0) + ' · 최고 ' + starsText(rec?.bestStars ?? 0);
}

// ── 지역 안내 카드 ───────────────────────────────────
export type TierTone = 'easy' | 'hard';
/** 난이도 색 구분용(글자 '입문'·'도전'도 함께 보여 주므로 색만으로 구분하지 않는다) */
export function tierTone(info: Pick<RegionInfo, 'tier'>): TierTone {
  return info.tier === '도전' ? 'hard' : 'easy';
}

export interface RewardText {
  /** 클리어 보상 식 */
  readonly formula: string;
  /** 카드 범위 */
  readonly card: string;
}
/** 마을 카드·결과 화면의 보상 범위 문구(지역 값 그대로) */
export function rewardText(info: Pick<RegionInfo, 'reward'>, mult = 1): RewardText {
  // mult: 난이도 골드 배율(하드 ×3, D3b — 게임 stageClear와 같은 값). 노말 1
  const r = info.reward, m = (n: number): number => Math.round(n * mult);
  return {
    formula: '클리어 ' + fmtNum(m(r.base)) + ' + 처치당 ' + fmtNum(m(r.perKill)) + ' 골드',
    card: '카드 ' + fmtNum(m(r.cardMin)) + '~' + fmtNum(m(r.cardMax)) + ' 골드',
  };
}

export interface RegionCardText {
  readonly id: RegionId;
  readonly name: string;
  readonly tier: string;
  readonly tone: TierTone;
  readonly blurb: string;
  readonly enemies: string;
  /** 위험 요소 줄들(없으면 '없음' 한 줄) */
  readonly hazards: readonly string[];
  readonly boss: string;
  readonly recommend: string;
  readonly reward: string;
  readonly record: string;
  readonly hint: string;
}

// ── 스테이지 줄(D3a) ─────────────────────────────────
export interface StageLine {
  /** 지역이 잠겼는지(n-10 전) */
  readonly locked: boolean;
  /** '3-4' 꼴(잠겼으면 '') */
  readonly label: string;
  /** '일반'·'중간보스'·'지역보스' */
  readonly kind: string;
  /** 한 줄 설명: '중간보스까지 1 · 클리어 2회 ★★☆' 또는 잠김 안내 */
  readonly detail: string;
  readonly index: number;
  /** 고를 수 있는 마지막 번호(잠겼으면 0) */
  readonly max: number;
  readonly canPrev: boolean;
  readonly canNext: boolean;
  /** 하드 스테이지 줄인지(D3b) — ◀ ▶가 하드 고르기를 보낸다 */
  readonly hard?: boolean;
}
/** 진행 뷰(필요한 것만) */
export interface ProgressLike {
  selected(region: RegionId): string | null;
  regionUnlocked(region: RegionId): boolean;
  unlocked(id: string): boolean;
  stageClears(id: string): number;
  stageStars(id: string): number;
}
/** 문 앞 카드의 스테이지 줄: 그 지역에서 들어갈 스테이지, 종류, 보스까지 남은 수, 기록. 잠긴 지역은 여는 조건 */
export function stageLine(progress: ProgressLike | undefined, region: RegionId, prefix: 'r' | 'h' = 'r'): StageLine {
  const no = REGION_IDS.indexOf(region) + 1;
  const sel = progress ? progress.selected(region) : (region === 'r1' ? 'r1-s01' : null);
  if (!sel) {
    const prev = REGIONS[REGION_IDS[Math.max(0, no - 2)]];
    return { locked: true, label: '', kind: '', detail: `${prev.name} ${no - 1}-10 지역보스를 쓰러뜨리면 열린다`, index: 0, max: 0, canPrev: false, canNext: false };
  }
  const index = Number(sel.slice(-2));
  let max = index;
  while (max < 10 && progress && progress.unlocked(`${prefix}${no}-s${String(max + 1).padStart(2, '0')}`)) max++;
  const kind = index === 10 ? '지역보스' : index === 5 ? '중간보스' : '일반';
  const toBoss = index < 5 ? 5 - index : index < 10 ? 10 - index : 0;
  const clears = progress ? progress.stageClears(sel) : 0, stars = progress ? progress.stageStars(sel) : 0;
  const head = toBoss > 0 ? `${toBoss === 1 ? '다음' : toBoss + '개 뒤'} ${index < 5 ? '중간보스' : '지역보스'}` : '지역보스 스테이지';
  const rec = clears > 0 ? `클리어 ${fmtNum(clears)}회 ${starsText(stars)}` : '첫 도전';
  const hard = prefix === 'h';
  return { locked: false, label: `${hard ? '하드 ' : ''}${no}-${index}`, kind, detail: `${kind === '일반' ? head : kind === '중간보스' ? '중간보스 스테이지' : head} · ${rec}`, index, max, canPrev: index > 1, canNext: index < max, ...(hard ? { hard } : {}) };
}

/**
 * 문 앞 카드 스테이지 줄(D3b 난이도 포함): 문 난이도가 하드이고 그 지역 하드가 열렸으면 하드 줄, 아니면 노말 줄.
 * 하드를 골랐는데 그 지역 하드가 잠겼으면 노말 줄 앞에 '하드 잠김'을 붙인다(문은 노말로 들어간다 — game.doorStage와 같은 규칙)
 */
export function doorStageLine(progress: ProgressLike | undefined, hard: (ProgressLike & { readonly on: boolean }) | undefined, region: RegionId): StageLine {
  if (hard && hard.on && hard.regionUnlocked(region)) return stageLine(hard, region, 'h');
  const line = stageLine(progress, region);
  if (!hard || !hard.on || line.locked) return line;
  const no = REGION_IDS.indexOf(region) + 1;
  return { ...line, detail: `하드 잠김(노말 ${no}-10·하드 ${no > 1 ? no - 1 + '-10' : '없음'} 뒤) · 노말 입장` };
}

/** 마을 문 앞 카드의 모든 문구. RegionInfo의 플레이어 문구 필드와 기록만 쓴다(qaNote 제외) */
export function regionCardText(info: RegionInfo, rec: RegionRecordView | undefined): RegionCardText {
  const rw = rewardText(info);
  return {
    id: info.id,
    name: info.name,
    tier: info.tier,
    tone: tierTone(info),
    blurb: info.blurb,
    enemies: info.enemies.join(' · '),
    hazards: info.hazards.length > 0 ? info.hazards : ['없음'],
    boss: info.boss,
    recommend: info.recommend,
    reward: rw.formula + ' · ' + rw.card,
    record: recordText(rec),
    hint: '문으로 걸어 들어가면 입장합니다',
  };
}

export interface DoorGuide {
  readonly id: RegionId;
  /** 문 위치 말(문이 둘 이상일 때): 문마다 방위가 다르면 시작 자리 기준 8방위('북서쪽' 등), 모두 같은 방위면 왼쪽/오른쪽. 문이 하나면 '' */
  readonly side: string;
  readonly name: string;
  readonly tier: string;
  readonly tone: TierTone;
}
const COMPASS = ['북쪽', '북동쪽', '동쪽', '남동쪽', '남쪽', '남서쪽', '서쪽', '북서쪽'] as const;
/** (x, z)에서 본 점 (tx, tz)의 8방위 말(북쪽 = −z). 같은 점이면 '' */
export function compassWord(x: number, z: number, tx: number, tz: number): string {
  const dx = tx - x, dz = tz - z;
  if (dx === 0 && dz === 0) return '';
  // 북쪽에서 시계 방향 각도(동쪽 +90°)
  const deg = (Math.atan2(dx, -dz) * 180) / Math.PI;
  return COMPASS[((Math.round(deg / 45) % 8) + 8) % 8];
}

/**
 * 마을 문 안내(퀘스트 창 문 줄): 지역 문을 화면 왼쪽→오른쪽 순서로(문 중심 x 기준). 문 위치 말은 시작 자리(from) 기준 방위 —
 * 마을②~④는 문 자리가 마을마다 달라(STAGE-WORLD-PRD 5.4) '북서쪽'·'동쪽'처럼 말하고, 두 문이 같은 방위(마을① 북쪽 두 문)면 왼쪽/오른쪽
 */
export function villageDoorGuide(exits: readonly { readonly rect: Rect; readonly to: MapId }[], from: { readonly x: number; readonly z: number } = TOWN_SPAWN): DoorGuide[] {
  const list: { id: RegionId; cx: number; dir: string }[] = [];
  for (const e of exits) if (isRegionId(e.to)) {
    const cx = (e.rect.x0 + e.rect.x1) / 2, cz = (e.rect.z0 + e.rect.z1) / 2;
    list.push({ id: e.to, cx, dir: compassWord(from.x, from.z, cx, cz) });
  }
  list.sort((a, b) => a.cx - b.cx);
  const sameDir = list.every((d) => d.dir === list[0].dir);
  return list.map((d, i) => {
    const info = REGIONS[d.id];
    const side = list.length < 2 ? '' : sameDir ? (i === 0 ? '왼쪽' : '오른쪽') : d.dir;
    return { id: d.id, side, name: info.name, tier: info.tier, tone: tierTone(info) };
  });
}

// ── 결과 화면 ────────────────────────────────────────
export interface RewardBreakdown {
  /** 클리어 기본 보상 */
  readonly base: number;
  readonly kills: number;
  readonly perKill: number;
  /** 처치 보상(kills × perKill) */
  readonly killGold: number;
  /** 기본 + 처치 */
  readonly subtotal: number;
  /** 고른 카드 골드, 아직 고르지 않았으면 -1 */
  readonly card: number;
  /** 카드까지 더한 총합(카드를 고르기 전에는 subtotal) */
  readonly total: number;
  /** S3: 클리어 base·카드에 곱한 스테이지 배율(1 = 없음)과 첫 클리어 골드(이미 subtotal·total에 들어 있다) */
  readonly mult: number;
  readonly firstGold: number;
  /** 이번 판 스페셜 몬스터 처치 골드(처치 순간 이미 지급됐고 subtotal에는 들어 있지 않다. total에는 더해 실제 받은 합과 맞춘다) */
  readonly special: number;
}
/** 지역 보상 값으로 계산한 보상 내역. cardGold < 0이면 카드 미선택 */
export function rewardBreakdown(info: Pick<RegionInfo, 'reward'>, kills: number, cardGold: number, mult = 1, firstGold = 0, diff = 1, specialGold = 0): RewardBreakdown {
  // mult: S3 스테이지 배율(클리어 base·카드에만), diff: 난이도 골드 배율(하드 ×3, D3b — 기본·처치 모두). 게임 stageClear처럼
  // (기본 × 스테이지 배율 + 처치 × 처치당) × 난이도를 반올림하고, 첫 클리어 골드(난이도 배율 없음)를 더한다
  const k = safeCount(kills);
  const m = Number.isFinite(mult) && mult > 0 ? mult : 1, d = Number.isFinite(diff) && diff > 0 ? diff : 1, first = safeCount(firstGold);
  const base = Math.round(info.reward.base * m * d), perKill = Math.round(info.reward.perKill * d);
  const earned = Math.round((info.reward.base * m + k * info.reward.perKill) * d);
  const killGold = earned - base;
  const subtotal = earned + first;
  const card = Number.isFinite(cardGold) && cardGold >= 0 ? cardGold : -1;
  const special = safeCount(specialGold);
  return { base, kills: k, perKill, killGold, subtotal, card, total: subtotal + special + (card >= 0 ? card : 0), mult: m, firstGold: first, special };
}
/**
 * 결과 화면 '획득 골드' 통계 값. result.gold에는 처치 순간 이미 지급한 스페셜 골드가 없으므로 더해서
 * 보상 내역(rewardBreakdown)의 '스페셜 +N' 합과 같은 수가 되게 한다(PR #175 5차 리뷰 P2). 카드 골드는 별도 항목이라 여기에 없다.
 */
export function resultGoldStat(gold: number, specialGold: number): number {
  return safeCount(gold) + safeCount(specialGold);
}
export function rewardBreakdownText(b: RewardBreakdown): string {
  let s = '클리어 ' + fmtNum(b.base) + (b.mult > 1 ? ' (×' + b.mult + ')' : '') + ' + 처치 ' + fmtNum(b.kills) + '×' + fmtNum(b.perKill);
  if (b.firstGold > 0) s += ' + 첫 클리어 ' + fmtNum(b.firstGold);
  if (b.special > 0) s += ' + 스페셜 ' + fmtNum(b.special);
  s += ' = ' + fmtNum(b.subtotal + b.special) + ' 골드';
  if (b.card >= 0) s += ' + 카드 ' + fmtNum(b.card) + ' = ' + fmtNum(b.total) + ' 골드';
  return s;
}

/** 결과·패배의 '다시 도전' 버튼 글자(같은 지역을 다시 한다) */
export function retryLabel(mapId: MapId): string {
  return isRegionId(mapId) ? '다시 도전 (' + REGIONS[mapId].name + ')' : '다시 도전';
}

export const NEW_FIRST = 1, NEW_TIME = 2, NEW_STARS = 4;
/**
 * 이번 클리어가 이전 기록을 넘었는지(비트). base는 이번 판이 시작되기 전의 기록.
 * 첫 클리어 = 클리어 수도 기록 시간도 없던 지역. 시간은 기록이 없었거나 이번 틱이 더 짧을 때, 별은 이번이 더 클 때.
 */
export function recordFlags(base: RegionRecordView, cleared: boolean, clearTicks: number, stars: number): number {
  if (!cleared) return 0;
  let f = 0;
  const best = safeCount(base.bestClearTicks);
  if (safeCount(base.clears) === 0 && best === 0) f |= NEW_FIRST;
  if (Number.isFinite(clearTicks) && clearTicks > 0 && (best === 0 || clearTicks < best)) f |= NEW_TIME;
  if (stars > safeCount(base.bestStars)) f |= NEW_STARS;
  return f;
}

/**
 * 결과 진입 시점의 이전 최고 기록을 기억한다. 게임은 클리어하는 틱에 기록을 저장하므로 결과 화면에서 읽는 view.records는 이미 갱신돼 있다.
 * 그래서 결과가 아닌 장면(타이틀·마을·던전)에서 매 프레임 기록을 복사해 두고, 결과·패배에서는 복사를 멈춰 '판 시작 전 기록'을 남긴다.
 * 고정 슬롯에 숫자만 복사하므로 할당이 없다.
 */
export class RecordWatch {
  private readonly clears = new Float64Array(REGION_IDS.length);
  private readonly ticks = new Float64Array(REGION_IDS.length);
  private readonly stars = new Float64Array(REGION_IDS.length);
  private readonly seen = new Uint8Array(REGION_IDS.length);
  private readonly tmp = { clears: 0, bestClearTicks: 0, bestStars: 0 };

  observe(scene: number, records: Readonly<Record<RegionId, RegionRecordView | undefined>>): void {
    if (scene === Scene.Result || scene === Scene.Defeat) return;
    for (let i = 0; i < REGION_IDS.length; i++) {
      const r = records[REGION_IDS[i]];
      if (!r) continue;
      this.clears[i] = safeCount(r.clears);
      this.ticks[i] = safeCount(r.bestClearTicks);
      this.stars[i] = safeCount(r.bestStars);
      this.seen[i] = 1;
    }
  }

  /** 이번 판 결과가 낸 새 기록 비트. 판 시작 전 기록을 본 적이 없으면 0(확신할 수 없으면 표시하지 않는다) */
  flags(region: MapId, cleared: boolean, clearTicks: number, stars: number): number {
    if (!isRegionId(region)) return 0;
    const i = REGION_IDS.indexOf(region);
    if (i < 0 || !this.seen[i]) return 0;
    const t = this.tmp;
    t.clears = this.clears[i]; t.bestClearTicks = this.ticks[i]; t.bestStars = this.stars[i];
    return recordFlags(t, cleared, clearTicks, stars);
  }
}

// ── 방 요령 토스트 ───────────────────────────────────
/** 요령 토스트가 머무는 시간(ms): 4~5초 */
export const ROOM_TIP_MS = 4500;

/** 이 지역·방의 요령 문구(없으면 null) */
export function roomTipFor(mapId: MapId, room: number): string | null {
  if (!isRegionId(mapId) || !Number.isInteger(room) || room < 0) return null;
  const t = REGIONS[mapId].roomTips[room];
  return t ? t : null;
}

/**
 * 방에 처음 들어갈 때 한 번만 요령을 낸다. 같은 판(sceneSerial) 안에서 같은 방의 RoomEnter가 또 와도 다시 내지 않고,
 * 재도전·재입장(sceneSerial 증가)에서는 다시 낸다.
 */
export class RoomTipGate {
  private serial = -1;
  private shown = 0;

  take(mapId: MapId, room: number, serial: number): string | null {
    const tip = roomTipFor(mapId, room);
    if (!tip) return null;
    if (serial !== this.serial) { this.serial = serial; this.shown = 0; }
    const bit = 1 << Math.min(room, 30);
    if (this.shown & bit) return null;
    this.shown |= bit;
    return tip;
  }
}

/** 대상 표시의 몹 이름표. 마을·알 수 없는 맵은 R1 이름 그대로 */
export function mobNamesFor(mapId: MapId): readonly string[] {
  return isRegionId(mapId) ? REGIONS[mapId].mobNames : REGIONS.r1.mobNames;
}

/**
 * 하드 문(D3b)일 때 카드 문구: 테두리 색·권장·보상 줄을 하드 값으로(배지는 지역 등급 그대로 — 하드 표시는 난이도 버튼)(몹 레벨 = world.mobLevel 하드, 보상 배율 = economy DIFFICULTY_LOOT·DIFFICULTY_ILVL_ADD).
 * 나머지(적·위험·보스·기록)는 같은 지도라 그대로. 줄 길이는 노말 문구보다 짧게(카드 배치가 접히지 않게)
 */
export function hardCardText(t: RegionCardText, hard?: Pick<ProgressLike, 'stageClears' | 'stageStars'>): RegionCardText {
  const no = REGION_IDS.indexOf(t.id) + 1;
  const L = DIFFICULTY_LOOT.hard;
  // 내 기록 = 그 지역 하드 스테이지 기록 합(하드 진행 저장). 노말 지역 기록은 하드 판을 세지 않는다(PR #176 리뷰 P2)
  let clears = 0, stars = 0;
  if (hard) for (let i = 1; i <= 10; i++) { const id = `h${no}-s${String(i).padStart(2, '0')}`; clears += hard.stageClears(id); stars = Math.max(stars, hard.stageStars(id)); }
  return {
    ...t, tone: 'hard', record: `하드 클리어 ${fmtNum(clears)}회 · 최고 ${starsText(stars)}`,
    recommend: `하드 몹 Lv ${mobLevel(no as 1, 'hard')}`,
    // 보석은 드롭하는 지역(지역3부터)에서만 하드 등급 +1을 적는다(PR #176 리뷰 P2)
    reward: `골드 ×${L.gold} · 강화석 ×${L.stones} · 장비 Lv +${DIFFICULTY_ILVL_ADD.hard}` + (GEM_DROP.regionTier[no - 1] > 0 ? ` · 보석 +${GEM_DROP.diffAdd.hard}` : ''),
  };
}
